import { useEffect, useState } from "react";
import type { Product, PublicConfig, SiteSettings } from "../shared/types";
import { validateCartLine, validatePersonalisation } from "../shared/pricing";
import { loadStorefront } from "./api";
import { CartProvider, useCart } from "./cart";
import { Layout, Loading } from "./components";
import { AboutPage, CartPage, ContactPage, EnquiryPage, FaqPage, HomePage, NotFoundPage, PolicyPage, ProductPage, ShopPage, SuccessPage } from "./pages";
import { StudioPage } from "./studio";

type StorefrontState = {
  products: Product[];
  settings: SiteSettings;
  config: PublicConfig;
};

function usePath() {
  const [path, setPath] = useState(() => window.location.pathname);
  useEffect(() => {
    const update = () => setPath(window.location.pathname);
    window.addEventListener("popstate", update);
    return () => window.removeEventListener("popstate", update);
  }, []);
  return path.replace(/\/$/, "") || "/";
}

function WebMcpTools({ products }: { products: Product[] }) {
  const cart = useCart();
  useEffect(() => {
    const context = (document as Document & { modelContext?: { registerTool: (tool: Record<string, unknown>, options?: { signal?: AbortSignal }) => void | Promise<void> } }).modelContext;
    if (!context?.registerTool) return;
    const lifecycle = new AbortController();
    const report = () => undefined;
    const registrations = [
      context.registerTool({
        name: "list_pressed_studio_products",
        title: "List Pressed Studio products",
        description: "List the currently visible Pressed Studio Signs products with available configurations and AUD prices.",
        inputSchema: { type: "object", properties: {}, additionalProperties: false },
        annotations: { readOnlyHint: true, untrustedContentHint: false },
        execute: () => products.filter((product) => product.active).map((product) => ({ name: product.name, slug: product.slug, variants: product.variants.filter((variant) => variant.available).map((variant) => ({ id: variant.id, label: variant.label, priceCents: variant.priceCents })) }))
      }, { signal: lifecycle.signal }),
      context.registerTool({
        name: "view_pressed_studio_cart",
        title: "View Pressed Studio bag",
        description: "Read the personalised items currently in the shopper's bag.",
        inputSchema: { type: "object", properties: {}, additionalProperties: false },
        annotations: { readOnlyHint: true, untrustedContentHint: false },
        execute: () => ({ itemCount: cart.count, lines: cart.lines })
      }, { signal: lifecycle.signal }),
      context.registerTool({
        name: "add_configured_pressed_studio_item",
        title: "Add configured sign to bag",
        description: "Add one fully configured Pressed Studio sign to the shopper's bag after validating its variant, wording and style.",
        inputSchema: {
          type: "object",
          properties: {
            productSlug: { type: "string" },
            variantId: { type: "string" },
            quantity: { type: "integer", minimum: 1, maximum: 10 },
            style: { type: "string" },
            wording: { type: "string" },
            wordingOne: { type: "string" },
            wordingTwo: { type: "string" },
            requestedDate: { type: "string" },
            note: { type: "string" },
            wordingConfirmed: { type: "boolean" }
          },
          required: ["productSlug", "variantId", "quantity", "style", "wordingConfirmed"],
          additionalProperties: false
        },
        annotations: { readOnlyHint: false, untrustedContentHint: false },
        execute: (input: unknown) => {
          const value = input as Record<string, unknown>;
          const product = products.find((candidate) => candidate.slug === value.productSlug && candidate.active);
          if (!product) throw new Error("Product not found.");
          const variant = product.variants.find((candidate) => candidate.id === value.variantId && candidate.available);
          if (!variant) throw new Error("Configuration not available.");
          const personalisation = {
            style: String(value.style || ""),
            wording: value.wording ? String(value.wording) : "",
            wordingOne: value.wordingOne ? String(value.wordingOne) : "",
            wordingTwo: value.wordingTwo ? String(value.wordingTwo) : "",
            requestedDate: value.requestedDate ? String(value.requestedDate) : "",
            note: value.note ? String(value.note) : "",
            wordingConfirmed: value.wordingConfirmed === true
          };
          validatePersonalisation(product, personalisation);
          const line = { id: "webmcp-preview", productId: product.id, variantId: variant.id, quantity: Number(value.quantity), personalisation };
          validateCartLine(product, line);
          const id = cart.addLine({ productId: line.productId, variantId: line.variantId, quantity: line.quantity, personalisation: line.personalisation });
          return { id, product: product.name, configuration: variant.label, added: true };
        }
      }, { signal: lifecycle.signal })
    ];
    registrations.forEach((registration) => Promise.resolve(registration).catch(report));
    return () => lifecycle.abort();
  }, [products, cart]);
  return null;
}

function StorefrontApp() {
  const path = usePath();
  const [state, setState] = useState<StorefrontState | null>(null);
  useEffect(() => { void loadStorefront().then(setState); }, []);

  if (path === "/studio" || path.startsWith("/studio/")) return <StudioPage />;
  if (!state) return <div className="app-loading"><Loading /></div>;

  const { products, settings, config } = state;
  let page;
  if (path === "/") page = <HomePage products={products} />;
  else if (path === "/shop") page = <ShopPage products={products} />;
  else if (path.startsWith("/product/")) {
    const slug = decodeURIComponent(path.split("/").filter(Boolean)[1] || "");
    const product = products.find((item) => item.slug === slug && item.active);
    page = product ? <ProductPage key={product.id} product={product} related={products.filter((item) => item.id !== product.id && item.active)} /> : <NotFoundPage />;
  } else if (path === "/cart") page = <CartPage products={products} settings={settings} config={config} />;
  else if (path === "/custom-signs") page = <EnquiryPage config={config} />;
  else if (path === "/contact") page = <ContactPage config={config} />;
  else if (path === "/about") page = <AboutPage />;
  else if (path === "/faq") page = <FaqPage />;
  else if (path.startsWith("/policies/")) page = <PolicyPage type={path.split("/")[2] || ""} settings={settings} />;
  else if (path === "/order/success") page = <SuccessPage />;
  else page = <NotFoundPage />;

  return <Layout settings={settings}><WebMcpTools products={products} />{page}</Layout>;
}

export function App() {
  return <CartProvider><StorefrontApp /></CartProvider>;
}
